import { ProductSpec, ReadDealResponse } from '@/types'
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom'
import { useAcceptProposal } from '@/api/accounting-dealer'
import { ApiError } from '@/api/http-client'
import { formatCurrency, formatPercentage, toast } from '@/helpers'

import { Box, Card, Divider, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { routes } from '@/router'
import { useQueryClient } from '@tanstack/react-query'

export function ProposalPage() {
  const { deal, proposal } = useOutletContext<ReadDealResponse>()

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutate: acceptProposal, isPending } = useAcceptProposal({
    onSuccess: () => {
      navigate(
        generatePath(routes.accounting.hiring.mainPartner, { deal_key: deal.key }),
      )
      queryClient.invalidateQueries({ queryKey: ['deal', deal.key] })
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'proposal_expired') {
        toast.error(
          'Esta proposta expirou. Fale com o comercial para gerar uma nova.',
        )
        return
      }

      toast.error(error.message || 'Não foi possível aceitar a proposta')
    },
  })

  if (!proposal) {
    return <ProposalPlanNotFound />
  }

  const proposalPlan = proposal.product_specs.find(
    ({ product }) => product.category === 'plan',
  )

  if (!proposalPlan) {
    return <ProposalPlanNotFound />
  }

  const { price_amount, min_price_amount, price_rate, product } = proposalPlan
  const isChangingAccountant = deal.company.status === 'changing_accountant'
  const extraSpecs = proposal.product_specs.filter(
    ({ product: specProduct }) =>
      specProduct.category === 'item' || specProduct.category === 'service',
  )

  return (
    <Box textAlign="center">
      <Typography variant="h3" mb={4} mt={4}>
        Confira sua proposta
      </Typography>

      <Card
        ml={'auto'}
        mr={'auto'}
        component={Stack}
        spacing={2}
        maxWidth={450}
        padding="24px !important"
      >
        <Typography variant="h4">{product.name}</Typography>
        {price_rate && price_rate > 0 && (
          <Typography>
            {formatPercentage(price_rate)} do seu faturamento, a partir de:
          </Typography>
        )}
        <Typography variant="h2">
          {formatCurrency(price_amount || min_price_amount)} / mês
        </Typography>

        <Divider />

        <Typography variant="h6">Assessoria contábil completa</Typography>

        <Divider />

        <Box textAlign="left">
          <Typography>Serviços incluídos:</Typography>
          <Box component="ul" m={0}>
            <li>Emissão de notas fiscais, sem limites no mês.</li>
            <li>Atendimento por WhatsApp.</li>
            <li>Suporte no cadastro em hospitais e convênios.</li>
          </Box>
        </Box>

        {extraSpecs.length > 0 && (
          <>
            <Divider />
            <Box textAlign="left">
              <Typography>Produtos da proposta:</Typography>
              <Box component="ul" m={0}>
                {extraSpecs.map((spec) => (
                  <li key={spec.key}>
                    {spec.product.name} — {formatProductSpecPrice(spec)}
                  </li>
                ))}
              </Box>
            </Box>
          </>
        )}

        {!isChangingAccountant && (
          <>
            <Divider />
            <Box textAlign="left">
              <Typography>Abertura da PJ:</Typography>
              <Box component="ul" m={0}>
                <li>Não cobramos honorários pela abertura.</li>
                <li>
                  Você paga apenas as taxas da junta comercial, prefeitura e o
                  registro da PJ no CRM.
                </li>
              </Box>
            </Box>
          </>
        )}
      </Card>
      <Box mt={4}>
        <LoadingButton
          type="submit"
          size="large"
          loading={isPending}
          onClick={() => acceptProposal(deal.key)}
        >
          Aceitar proposta
        </LoadingButton>
      </Box>
    </Box>
  )
}

function formatProductSpecPrice(spec: ProductSpec): string {
  if (spec.price_type === 'percentage' && spec.price_rate) {
    return `${formatPercentage(spec.price_rate)} · a partir de ${formatCurrency(
      spec.min_price_amount,
    )}`
  }

  const amount = spec.price_amount || spec.min_price_amount
  const formatted = formatCurrency(amount)
  if (spec.recurrence_type === 'monthly') return `${formatted} / mês`
  if (spec.recurrence_type === 'yearly') return `${formatted} / ano`
  return formatted
}

function ProposalPlanNotFound() {
  return (
    <Box textAlign="center">
      <Typography variant="h3" mb={4}>
        Nenhum plano encontrado, <br /> entre em contato com o suporte.
      </Typography>
    </Box>
  )
}
